import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  DND_TOUCH_ACTIVATION,
  Separator,
  columnOrderWithIdAtEnd,
  getColumnDefId,
  mergeColumnOrder,
  type DataTableColumnDef,
  type IconName,
  type VisibilityState,
} from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import {
  ISSUE_CF_COLUMN_PREFIX,
  issueCustomFieldColumnId,
} from "#app/components/issues/issue-custom-field-columns";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import { CreateFieldPage, FieldsPicker } from "./add-field-page";
import {
  ItemRow,
  PinnedRow,
  SectionHeader,
  SortableItemRow,
  type DisplayItem,
} from "./display-field-rows";
import { useTrackViewOption } from "#app/lib/views/view-context";
import { useFieldsPageStep } from "./options-panel-slots";

// ---------------------------------------------------------------------------
// ListDisplayFields — the Fields page of a LIST view's display options
// ---------------------------------------------------------------------------
//
// A list's "fields" are its columns, so this drives the column state the
// table renders from — inside a saved view that is the view draft (a toggle
// here raises Save), elsewhere the per-user pool: search up top, then the
// pinned name row, a "Shown" group in table order (drag to reorder — the
// order IS the column order), and per-kind groups of hidden columns — the
// "Add existing" tab of the `FieldsPicker` the page opens on.
//
// Body only. The panel that hosts it owns the surface, the scroll and the
// header; this renders in flow. The props past `columns`/`fields` spread
// straight from the host's column-state props — the same contract the
// popover `DataTableColumnSettings` takes, so the two editors can never
// disagree on what they persist.
// ---------------------------------------------------------------------------

/** Panel treatment for the built-in columns: the leading type icon, plus a
 *  panel label for any column whose header can't be reused verbatim. */
const BUILTIN_COLUMN_META: Record<string, { label?: string; icon: IconName }> =
  {
    name: { icon: "type" },
    stage: { icon: PROPERTY_ICON.stage },
    age: { icon: PROPERTY_ICON.followUp },
    assignees: { icon: ASSIGNEE_ICON },
    teams: { icon: PROPERTY_ICON.team },
    address: { icon: PROPERTY_ICON.address },
    issueType: { icon: PROPERTY_ICON.issueType },
    workflow: { icon: PROPERTY_ICON.workflow },
    issueSource: { icon: PROPERTY_ICON.issueSource },
    creationDate: { icon: "calendar" },
    lastUpdateDate: { icon: "calendar-clock" },
  };

export type ListDisplayFieldsProps<TData> = {
  /** The same column definitions passed to the DataTable. */
  columns: DataTableColumnDef<TData>[];
  /** ALL workspace custom fields (hidden ones must stay listed). */
  fields: CustomFieldDef[];
  /** Pinned ids render locked at the top — not hideable, not reorderable. */
  pinnedColumnIds?: string[];
  columnOrder: string[];
  columnVisibility: VisibilityState;
  onColumnOrderChange: (order: string[]) => void;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
};

type ListDisplayItems = {
  fullOrder: string[];
  pinnedItems: DisplayItem[];
  items: DisplayItem[];
};

/**
 * The column roster as display items, in table order: the pinned rows apart,
 * every other known column with its shown state. Shared by the page and by
 * `countShownListFields`, so the main page's count and the Shown group can
 * never disagree.
 */
function listDisplayItems<TData>({
  columns,
  fields,
  pinnedColumnIds,
  columnOrder,
  columnVisibility,
}: {
  columns: DataTableColumnDef<TData>[];
  fields: CustomFieldDef[];
  pinnedColumnIds: string[] | undefined;
  columnOrder: string[];
  columnVisibility: VisibilityState;
}): ListDisplayItems {
  const byId = new Map<string, DataTableColumnDef<TData>>();
  const defaultIds: string[] = [];
  for (const def of columns) {
    const id = getColumnDefId(def);
    if (id === undefined) continue;
    byId.set(id, def);
    defaultIds.push(id);
  }
  // Tolerate stale/partial orders the same way the table does. The merge
  // PRESERVES ids that aren't currently rendered (gated columns keep their
  // saved slots) — filter those for display, keep them in fullOrder so
  // commits round-trip them.
  const order = mergeColumnOrder(columnOrder, defaultIds);
  const known = order.filter((id) => byId.has(id));
  const pinnedSet = new Set(pinnedColumnIds ?? []);
  const fieldById = new Map(fields.map((f) => [f.customFieldId, f]));
  const toItem = (id: string): DisplayItem => {
    const shown = columnVisibility[id] !== false;
    if (id.startsWith(ISSUE_CF_COLUMN_PREFIX)) {
      const field = fieldById.get(id.slice(ISSUE_CF_COLUMN_PREFIX.length));
      const meta = field ? getFieldTypeMeta(field.type) : undefined;
      return {
        key: id,
        label: field?.name ?? id,
        icon: meta?.icon ?? "type",
        // Colored by field TYPE (the field-manager treatment) — properties
        // deliberately stay muted so user data reads distinct.
        ...(meta ? { iconColor: meta.color } : null),
        ...(field ? { field } : null),
        kind: "field",
        shown,
      };
    }
    const def = byId.get(id)!;
    const builtin = BUILTIN_COLUMN_META[id];
    return {
      key: id,
      label:
        builtin?.label ?? (typeof def.header === "string" ? def.header : id),
      icon: builtin?.icon ?? "type",
      kind: "property",
      shown,
    };
  };
  return {
    fullOrder: order,
    pinnedItems: known.filter((id) => pinnedSet.has(id)).map(toItem),
    items: known.filter((id) => !pinnedSet.has(id)).map(toItem),
  };
}

/** How many of a list's columns are shown, the pinned title column aside —
 *  the main page's Fields row readout, from the same roster the page lists. */
export function countShownListFields<TData>(
  props: Parameters<typeof listDisplayItems<TData>>[0],
): number {
  return listDisplayItems(props).items.filter((i) => i.shown).length;
}

export function ListDisplayFields<TData>({
  columns,
  fields,
  pinnedColumnIds,
  columnOrder,
  columnVisibility,
  onColumnOrderChange,
  onColumnVisibilityChange,
}: ListDisplayFieldsProps<TData>) {
  const [query, setQuery] = useState("");

  // --- Flatten the column roster into display items, in table order ---
  const { fullOrder, pinnedItems, items } = useMemo(
    () =>
      listDisplayItems({
        columns,
        fields,
        pinnedColumnIds,
        columnOrder,
        columnVisibility,
      }),
    [columns, columnOrder, columnVisibility, pinnedColumnIds, fields],
  );

  const q = query.trim().toLowerCase();
  const matches = useCallback(
    (item: DisplayItem) => !q || item.label.toLowerCase().includes(q),
    [q],
  );

  const shownItems = items.filter((i) => i.shown);
  const visiblePinned = pinnedItems.filter(matches);
  const visibleShown = shownItems.filter(matches);
  const visibleHiddenProps = items.filter(
    (i) => !i.shown && i.kind === "property" && matches(i),
  );
  const visibleHiddenFields = items.filter(
    (i) => !i.shown && i.kind === "field" && matches(i),
  );

  // --- Toggle + reorder handlers ---
  const visibleCount = pinnedItems.length + shownItems.length;
  const setShown = useCallback(
    (key: string, shown: boolean) => {
      // Never allow hiding the last visible column — a zero-column table
      // renders data rows with no cells and persists that way.
      if (!shown && visibleCount <= 1) return;
      // A shown column lands at the END of the order (columnOrderWithIdAtEnd
      // — the shared show policy) — both writes go out in one tick, which the
      // column-state hooks compose into one commit.
      if (shown) {
        onColumnOrderChange(columnOrderWithIdAtEnd(fullOrder, key));
      }
      onColumnVisibilityChange({ ...columnVisibility, [key]: shown });
    },
    [
      columnVisibility,
      onColumnVisibilityChange,
      onColumnOrderChange,
      fullOrder,
      visibleCount,
    ],
  );
  const trackOption = useTrackViewOption();
  const handleToggle = useCallback(
    (item: DisplayItem, shown: boolean) => {
      setShown(item.key, shown);
      trackOption("field", { fieldKind: item.kind, on: shown });
    },
    [setShown, trackOption],
  );

  // --- The create flow, when a shell hosts the page ---
  const fieldsStep = useFieldsPageStep();
  const setStep = fieldsStep?.setStep;
  // A created field shows once its column EXISTS. Custom-field columns are
  // hidden by default, and the view keeps only the default-hidden columns it
  // was told to show — a show written for a column the table has not grown
  // yet is not default-hidden yet, so it would be dropped, and the column
  // would arrive hidden. So the show waits for the refetched field list to
  // carry the field, riding the browse step (this body remounts on the way
  // back to the roster); the roster is back on screen meanwhile.
  const showCreated = useCallback(
    (customFieldId: string) => {
      setStep?.({
        kind: "browse",
        tab: "existing",
        createdFieldId: customFieldId,
      });
    },
    [setStep],
  );
  const pendingShowKey =
    fieldsStep?.step.kind === "browse" && fieldsStep.step.createdFieldId
      ? issueCustomFieldColumnId(fieldsStep.step.createdFieldId)
      : null;
  const pendingArrived =
    pendingShowKey !== null && items.some((i) => i.key === pendingShowKey);
  useEffect(() => {
    if (!pendingArrived || pendingShowKey === null) return;
    setShown(pendingShowKey, true);
    setStep?.({ kind: "browse", tab: "existing" });
  }, [pendingArrived, pendingShowKey, setShown, setStep]);

  // Reorder only meaningful on the unfiltered Shown list (indices match).
  // A mouse drags after 8px of travel, a finger after a hold.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );
  const reorderable = !q;
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const keys = shownItems.map((i) => i.key);
      const oldIndex = keys.indexOf(String(active.id));
      const newIndex = keys.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      // Rebuild the full order: the shown rows take the new sequence while
      // hidden and pinned ids keep their stored slots.
      const nextShown = arrayMove(keys, oldIndex, newIndex);
      const shownSet = new Set(nextShown);
      let cursor = 0;
      onColumnOrderChange(
        fullOrder.map((id) => (shownSet.has(id) ? nextShown[cursor++]! : id)),
      );
    },
    [shownItems, fullOrder, onColumnOrderChange],
  );

  if (fieldsStep?.step.kind === "create") {
    return (
      <CreateFieldPage
        type={fieldsStep.step.type}
        onCancel={() => fieldsStep.setStep({ kind: "browse", tab: "create" })}
        onCreated={showCreated}
      />
    );
  }

  const shownMatches = visiblePinned.length + visibleShown.length > 0;
  const rosterMatches =
    shownMatches ||
    visibleHiddenProps.length > 0 ||
    visibleHiddenFields.length > 0;

  return (
    <FieldsPicker
      query={query}
      onQueryChange={setQuery}
      initialTab={
        fieldsStep?.step.kind === "browse" ? fieldsStep.step.tab : undefined
      }
      rosterMatches={rosterMatches}
      onCreateType={(type) => fieldsStep?.setStep({ kind: "create", type })}
    >
      <>
        {/* Shown — pinned rows first, then the columns in table order. A
            query that leaves it empty drops the group; the picker owns the
            one empty state. */}
        {!q || shownMatches ? (
        <Box
          direction="column"
          paddingX="md"
          paddingY="sm"
          gap="2xs"
          role="list"
          aria-label="Shown"
        >
          <SectionHeader
            label="Shown"
            count={visiblePinned.length + visibleShown.length}
          />
          {visiblePinned.map((item) => (
            <PinnedRow key={item.key} label={item.label} icon={item.icon} />
          ))}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleShown.map((i) => i.key)}
              strategy={verticalListSortingStrategy}
            >
              {visibleShown.map((item) => (
                <SortableItemRow
                  key={item.key}
                  item={item}
                  sortable={reorderable}
                  onToggle={handleToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
        ) : null}

        {/* Hidden built-in columns */}
        {visibleHiddenProps.length > 0 ? (
          <>
            {!q || shownMatches ? <Separator /> : null}
            <Box
              direction="column"
              paddingX="md"
              paddingY="sm"
              gap="2xs"
              role="list"
              aria-label="Properties"
            >
              <SectionHeader
                label="Properties"
                count={visibleHiddenProps.length}
              />
              {visibleHiddenProps.map((item) => (
                <ItemRow key={item.key} item={item} onToggle={handleToggle} />
              ))}
            </Box>
          </>
        ) : null}

        {/* Hidden custom-field columns */}
        {visibleHiddenFields.length > 0 ? (
          <>
            {(!q || shownMatches) || visibleHiddenProps.length > 0 ? (
              <Separator />
            ) : null}
            <Box
              direction="column"
              paddingX="md"
              paddingY="sm"
              gap="2xs"
              role="list"
              aria-label="Custom Fields"
            >
              <SectionHeader
                label="Custom Fields"
                count={visibleHiddenFields.length}
              />
              {visibleHiddenFields.map((item) => (
                <ItemRow key={item.key} item={item} onToggle={handleToggle} />
              ))}
            </Box>
          </>
        ) : null}

      </>
    </FieldsPicker>
  );
}
